import * as Blockly from 'blockly/core';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldFunction } from '@/(routers)/boards/editor/block/definitions/fields/fieldFunction';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const COLOR = CATEGORIES.func.color;

const CHECK_MAP: Record<string, string> = {
    float: 'Number',
    String: 'String',
    bool: 'Boolean',
};

const SHADOW_MAP: Record<string, { type: string; field: string; value: string | number }> = {
    float: { type: 'number', field: 'NUM', value: 0 },
    String: { type: 'string', field: 'TEXT', value: '' },
    bool: { type: 'bool', field: 'BOOL', value: 'TRUE' },
};

function buildCallInputs(block: Blockly.Block): void {
    if (block.previousConnection?.isConnected()) {
        block.previousConnection.disconnect();
    }
    if (block.nextConnection?.isConnected()) {
        block.nextConnection.disconnect();
    }
    if (block.outputConnection?.isConnected()) {
        block.outputConnection.disconnect();
    }

    while (block.inputList.length > 0) {
        block.removeInput(block.inputList[0].name ?? '', true);
    }

    const fnId: string = (block as any)._fnId ?? '';
    const fnName: string = (block as any)._fnName ?? '作成した関数';
    const fn = useBlockFunctionStore.getState().functions.find((f) => f.id === fnId);
    const params = fn?.params ?? [];
    const returnType = fn?.returnType ?? null;

    if (returnType) {
        block.setPreviousStatement(false);
        block.setNextStatement(false);
        block.setOutput(true, CHECK_MAP[returnType]);
        const shapeMap: Record<string, number> = { float: 2, String: 3, bool: 1 };
        (block as Blockly.BlockSvg).setOutputShape?.(shapeMap[returnType] ?? 1);
    } else {
        block.setOutput(false);
        block.setPreviousStatement(true);
        block.setNextStatement(true);
    }

    const header = block.appendDummyInput('HEADER');
    header.appendField(new (Blockly.FieldImage as any)('/images/block/func_call.svg', 65, 65, 'icon'));
    header.appendField(new FieldStyledLabel('関数', { fontSize: 12, fontWeight: 'bold' }));
    header.appendField(new FieldStyledBadgeLabel('呼び出し'));
    header.appendField(new FieldStyledLabel(' ', {}));
    header.appendField(new FieldFunction(fnName), 'FN_LABEL');
    header.appendField(new FieldStyledLabel('を', {}));

    if (params.length === 0) {
        header.appendField(new FieldStyledLabel('で呼び出す ', { fontSize: 12 }));
        block.setInputsInline(false);
        return;
    }

    params.forEach((p, i) => {
        const shadow = SHADOW_MAP[p.type] ?? SHADOW_MAP['float'];
        const shadowDom = Blockly.utils.xml.createElement('shadow');
        shadowDom.setAttribute('type', shadow.type);
        const shadowField = Blockly.utils.xml.createElement('field');
        shadowField.setAttribute('name', shadow.field);
        shadowField.textContent = String(shadow.value);
        shadowDom.appendChild(shadowField);

        const input = block.appendValueInput(`ARG_${i}`);
        input.setCheck(CHECK_MAP[p.type] ?? 'Number');
        input.connection?.setShadowDom(shadowDom);
        input.appendField(new FieldStyledBadgeLabel(`引数${i + 1}`));
        if (i < params.length - 1) {
            block.appendDummyInput(`SPACE_${i}`).appendField(new FieldStyledLabel(' ', {}));
        }
    });

    block.appendDummyInput('FOOTER').appendField(new FieldStyledLabel('で呼び出す ', {}));

    block.setInputsInline(true);
}

defineMutableBlock({
    type: 'func_call',
    colour: COLOR,
    tooltip: '作成した関数を呼び出します',
    deletable: true,
    movable: true,
    previousStatement: true,
    nextStatement: true,

    init(this: Blockly.Block) {
        this.setColour(COLOR);
        this.setTooltip('作成した関数を呼び出します');
        this.setMovable(true);
        this.setDeletable(true);
        this.setPreviousStatement(true);
        this.setNextStatement(true);
        (this as any)._fnId = '';
        (this as any)._fnName = '作成した関数';
        (this as any)._params = [];
        buildCallInputs(this);

        requestAnimationFrame(() => {
            if ((this as any)._fnId) return;
            buildCallInputs(this);
        });
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('fn_id', (this as any)._fnId ?? '');
        container.setAttribute('fn_name', (this as any)._fnName ?? '');
        container.setAttribute('params', JSON.stringify((this as any)._params ?? []));
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        (this as any)._fnId = xml.getAttribute('fn_id') ?? '';
        (this as any)._fnName = xml.getAttribute('fn_name') ?? '作成した関数';
        try {
            (this as any)._params = JSON.parse(xml.getAttribute('params') ?? '[]');
        } catch {
            (this as any)._params = [];
        }
        buildCallInputs(this);
    },
});
